<script setup>
import { ref, nextTick, watch, useTemplateRef } from 'vue';
import { useResizeObserver } from '@vueuse/core';

// Filter values are shown in narrow chips/buttons, so a longer name gets clipped
// with an ellipsis. Expose the full value in a tooltip, but only when it is
// actually clipped — a tooltip repeating fully visible text is just noise.
const props = defineProps({
  label: {
    type: [String, Number],
    default: '',
  },
});

const labelRef = useTemplateRef('labelRef');
const isTruncated = ref(false);

const measure = () => {
  const el = labelRef.value;
  if (!el) {
    isTruncated.value = false;
    return;
  }

  // `scrollWidth`/`clientWidth` are rounded to whole pixels, so text that
  // overflows by a fraction of a pixel reports as fitting even though the
  // browser already paints an ellipsis ("marek 2" → "mare…"). Measuring the
  // text with a Range keeps the sub-pixel width.
  const range = document.createRange();
  range.selectNodeContents(el);
  const textWidth = range.getBoundingClientRect().width;

  isTruncated.value = textWidth - el.getBoundingClientRect().width > 0.05;
};

// Fires on mount too, which gives us the initial measurement.
useResizeObserver(labelRef, measure);
// The box can stay the same size while the text inside it changes.
watch(
  () => props.label,
  () => nextTick(measure)
);
</script>

<template>
  <!-- The tooltip sits on the wrapper, not on the clipped text, so hovering
  anywhere on the chip (icon and padding included) reveals the full value. -->
  <div v-tooltip.top="isTruncated ? label : ''">
    <slot name="icon" />
    <span ref="labelRef" class="truncate">{{ label }}</span>
  </div>
</template>
